import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdBadge,
  faListOl,
  faRadio,
  faTags,
  faComment,
  faMapMarkerAlt,
  faFire,
  faUsersCog,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { Task, OperationType, Feedback } from '../types';

type StatusScreen = { id: string; label: string; enabled: boolean; order: number };

type ChipItem = { id?: number; name?: string; text?: string };

interface SettingsPanelProps {
  maxTrupps: number;
  setMaxTrupps: (n: number) => void;
  quickTruppCreation: boolean;
  saveQuickTruppSetting: (enabled: boolean) => void;
  truppNamingMode: 'leader' | 'number';
  truppNamingModeLoading: boolean;
  saveTruppNamingMode: (mode: 'leader' | 'number') => void;
  orgShortName: string;
  orgShortNameDraft: string;
  setOrgShortNameDraft: (v: string) => void;
  orgShortNameLoading: boolean;
  orgShortNameSaving: boolean;
  orgShortNameError: string;
  setOrgShortNameError: (v: string) => void;
  saveOrgShortName: () => void;
  truppNamePrefixEnabled: boolean;
  truppNamePrefixLoading: boolean;
  saveTruppNamePrefixEnabled: (enabled: boolean) => void;
  statusUpdateScreens: StatusScreen[];
  statusUpdateScreensLoading: boolean;
  statusUpdateScreensSaving: boolean;
  statusUpdateScreensError: string;
  statusUpdateDragIndex: number | null;
  statusUpdateDragOverIndex: number | null;
  setStatusUpdateDragIndex: (n: number | null) => void;
  setStatusUpdateDragOverIndex: (n: number | null) => void;
  reorderStatusUpdateScreens: (from: number, to: number) => void;
  toggleStatusUpdateScreen: (index: number) => void;
  newFunkkanal: string;
  setNewFunkkanal: (v: string) => void;
  saveStandardFunkkanaele: () => void;
  radioChannelsWithIds: ChipItem[];
  deleteRadioChannel: (id: number, name: string) => void;
  newTask: string;
  setNewTask: (v: string) => void;
  saveStandardTasks: () => void;
  tasksWithIds: Task[];
  deleteTask: (id: number, name: string) => void;
  newFeedback: string;
  setNewFeedback: (v: string) => void;
  saveStandardFeedbacks: () => void;
  feedbacksWithIds: Feedback[];
  deleteFeedback: (id: number, text: string) => void;
  newStandort: string;
  setNewStandort: (v: string) => void;
  saveStandardStandorte: () => void;
  locationsWithIds: ChipItem[];
  deleteLocation: (id: number, name: string) => void;
  allOperationTypes: OperationType[];
  loadingOperationTypes: boolean;
  errorOperationTypes: string;
  addOperationTypeToOrg: (id: number) => void;
  removeOperationTypeFromOrg: (id: number) => void;
}

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #ececec',
  borderRadius: 14,
  padding: '18px 18px 16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  minWidth: 0,
};

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  color: '#9a9a9a',
  margin: '4px 0 2px',
};

const inputStyle: React.CSSProperties = {
  fontSize: 14,
  padding: '8px 12px',
  borderRadius: 10,
  border: '1.5px solid #e0e0e0',
  outline: 'none',
  background: '#fff',
  flex: 1,
  minWidth: 0,
};

const primaryBtn: React.CSSProperties = {
  background: '#c00',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '8px 14px',
  fontWeight: 700,
  cursor: 'pointer',
  flexShrink: 0,
};

const chipStyle: React.CSSProperties = {
  background: '#f4f4f4',
  borderRadius: 999,
  padding: '5px 10px 5px 12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  fontWeight: 600,
  color: '#333',
};

const SettingsCard: React.FC<{
  icon: any;
  title: string;
  description?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ icon, title, description, children, style }) => (
  <section className="portal-card" style={{ display: 'flex', flexDirection: 'column', gap: 14, ...style }}>
    <header className="portal-card-header" style={{ marginBottom: 0 }}>
      <div className="portal-card-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <h3 className="portal-card-title">{title}</h3>
        {description && <p className="portal-card-desc">{description}</p>}
      </div>
    </header>
    {children}
  </section>
);

const ToggleRow: React.FC<{
  id: string;
  checked: boolean;
  disabled?: boolean;
  title: string;
  hint?: string;
  onChange: (checked: boolean) => void;
}> = ({ id, checked, disabled, title, hint, onChange }) => (
  <label
    htmlFor={id}
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '12px 14px',
      borderRadius: 12,
      background: checked ? 'rgba(204,0,0,0.04)' : '#fafafa',
      border: `1px solid ${checked ? 'rgba(204,0,0,0.18)' : '#eee'}`,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.65 : 1,
      userSelect: 'none',
    }}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ width: 18, height: 18, marginTop: 1, accentColor: '#c00', flexShrink: 0 }}
    />
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontWeight: 700, fontSize: 14, color: '#222' }}>{title}</span>
      {hint && <span style={{ display: 'block', marginTop: 3, fontSize: 12, color: '#777', lineHeight: 1.4 }}>{hint}</span>}
    </span>
  </label>
);

const ChipList: React.FC<{
  items: { key: string | number; label: string; onRemove: () => void }[];
}> = ({ items }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: 28 }}>
    {items.length === 0 ? (
      <span style={{ fontSize: 13, color: '#aaa', fontStyle: 'italic' }}>Noch keine Einträge</span>
    ) : (
      items.map((item) => (
        <span key={item.key} style={chipStyle}>
          {item.label}
          <button
            type="button"
            onClick={item.onRemove}
            title="Entfernen"
            style={{
              background: 'none',
              border: 'none',
              color: '#c00',
              fontWeight: 900,
              fontSize: 15,
              lineHeight: 1,
              cursor: 'pointer',
              padding: 0,
              width: 18,
              height: 18,
              borderRadius: '50%',
            }}
          >
            ×
          </button>
        </span>
      ))
    )}
  </div>
);

const AddRow: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  placeholder: string;
}> = ({ value, onChange, onAdd, placeholder }) => (
  <div style={{ display: 'flex', gap: 8 }}>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
      onFocus={(e) => { e.currentTarget.style.borderColor = '#c00'; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && value.trim()) onAdd();
      }}
    />
    <button type="button" onClick={onAdd} className="portal-btn portal-btn--primary" title="Hinzufügen">
      <FontAwesomeIcon icon={faPlus} />
    </button>
  </div>
);

const SettingsPanel: React.FC<SettingsPanelProps> = (props) => {
  const prefixExample = `${props.orgShortName || 'KONO'} ${props.truppNamingMode === 'number' ? 'Trupp 01' : 'Trupp Müller'}`;

  return (
    <div className="portal-stack" style={{ gap: 22 }}>
      <div>
        <h2 className="portal-page-title">Einstellungen</h2>
        <p className="portal-page-subtitle">
          Organisationseinstellungen für Trupps, Statusabfragen und Stammdaten in der App.
        </p>
      </div>

      <div className="portal-section-label">Trupp & Organisation</div>
      <div className="portal-grid">
        <SettingsCard
          icon={faUsersCog}
          title="Trupp-Verhalten"
          description="Limits und Erstellung neuer Trupps im Einsatz."
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#444', marginBottom: 8 }}>
              Max. Trupps pro Einsatz
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={() => props.setMaxTrupps(Math.max(1, props.maxTrupps - 1))}
                style={{ ...primaryBtn, width: 36, padding: '8px 0' }}
              >
                −
              </button>
              <div style={{
                minWidth: 56,
                textAlign: 'center',
                fontSize: 18,
                fontWeight: 800,
                padding: '8px 12px',
                borderRadius: 10,
                border: '1.5px solid #c00',
                color: '#c00',
              }}>
                {props.maxTrupps}
              </div>
              <button
                type="button"
                onClick={() => props.setMaxTrupps(Math.min(20, props.maxTrupps + 1))}
                style={{ ...primaryBtn, width: 36, padding: '8px 0' }}
              >
                +
              </button>
            </div>
          </div>

          <ToggleRow
            id="quickTruppCreation"
            checked={props.quickTruppCreation}
            onChange={props.saveQuickTruppSetting}
            title="Schnell-Erstellung"
            hint="Trupps ohne Aufgabe, Funkkanal und Standort anlegen — Angaben folgen beim Start."
          />
        </SettingsCard>

        <SettingsCard
          icon={faIdBadge}
          title="Namensgebung"
          description="Wie Truppnamen gebildet und in der App angezeigt werden."
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#444', marginBottom: 8 }}>
              Truppname
            </div>
            <select
              value={props.truppNamingMode}
              disabled={props.truppNamingModeLoading}
              onChange={(e) => {
                const mode: 'leader' | 'number' = e.target.value === 'number' ? 'number' : 'leader';
                void props.saveTruppNamingMode(mode);
              }}
              style={{ ...inputStyle, width: '100%', borderColor: '#e0e0e0', cursor: 'pointer' }}
            >
              <option value="leader">Nach Truppführer (z.B. Trupp Müller)</option>
              <option value="number">Nach Nummer (z.B. Trupp 01)</option>
            </select>
            <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
              Bei «Nummer» kann der Name im Einsatz über Bearbeiten angepasst werden.
            </div>
          </div>

          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#444', marginBottom: 8 }}>
              Organisations-Kürzel
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                value={props.orgShortNameDraft}
                maxLength={10}
                disabled={props.orgShortNameLoading || props.orgShortNameSaving}
                onChange={(e) => {
                  props.setOrgShortNameDraft(e.target.value.toUpperCase());
                  props.setOrgShortNameError('');
                }}
                onBlur={() => {
                  if (props.orgShortNameDraft.trim() !== props.orgShortName) void props.saveOrgShortName();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void props.saveOrgShortName();
                  }
                }}
                placeholder="z.B. KONO"
                style={{
                  ...inputStyle,
                  minWidth: 120,
                  maxWidth: 180,
                  letterSpacing: 1.2,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                }}
              />
              <button
                type="button"
                onClick={() => void props.saveOrgShortName()}
                disabled={
                  props.orgShortNameLoading ||
                  props.orgShortNameSaving ||
                  props.orgShortNameDraft.trim() === props.orgShortName
                }
                style={{
                  ...primaryBtn,
                  opacity: props.orgShortNameDraft.trim() === props.orgShortName ? 0.45 : 1,
                  cursor: props.orgShortNameDraft.trim() === props.orgShortName ? 'not-allowed' : 'pointer',
                }}
              >
                Speichern
              </button>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
              Kurzform zur Unterscheidung bei gemeinsamen Funkkanälen.
            </div>
            {props.orgShortNameError && (
              <div style={{ marginTop: 6, color: '#c00', fontSize: 13, fontWeight: 600 }}>
                {props.orgShortNameError}
              </div>
            )}
          </div>

          <ToggleRow
            id="truppNamePrefix"
            checked={props.truppNamePrefixEnabled}
            disabled={props.truppNamePrefixLoading || !props.orgShortName}
            onChange={(checked) => void props.saveTruppNamePrefixEnabled(checked)}
            title="Kürzel als Prefix in der App"
            hint={
              props.orgShortName
                ? `Beispiel: ${prefixExample}`
                : 'Zuerst ein Organisations-Kürzel speichern.'
            }
          />
        </SettingsCard>
      </div>

      <div className="portal-section-label">Status-Update</div>
      <SettingsCard
        icon={faListOl}
        title="Screens beim Status-Update"
        description="Reihenfolge und Sichtbarkeit der Schritte in der App. Mindestens ein Screen muss aktiv bleiben."
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxWidth: 560,
          opacity: props.statusUpdateScreensLoading || props.statusUpdateScreensSaving ? 0.7 : 1,
        }}>
          {props.statusUpdateScreens.map((screen, index) => {
            const isDragging = props.statusUpdateDragIndex === index;
            const isDragOver = props.statusUpdateDragOverIndex === index && props.statusUpdateDragIndex !== index;
            return (
              <div
                key={screen.id}
                draggable={!props.statusUpdateScreensLoading && !props.statusUpdateScreensSaving}
                onDragStart={(e) => {
                  if (props.statusUpdateScreensLoading || props.statusUpdateScreensSaving) {
                    e.preventDefault();
                    return;
                  }
                  props.setStatusUpdateDragIndex(index);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(index));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (props.statusUpdateDragOverIndex !== index) props.setStatusUpdateDragOverIndex(index);
                }}
                onDragLeave={() => {
                  if (props.statusUpdateDragOverIndex === index) props.setStatusUpdateDragOverIndex(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromRaw = e.dataTransfer.getData('text/plain');
                  const fromIndex = Number.isFinite(Number(fromRaw)) ? Number(fromRaw) : props.statusUpdateDragIndex;
                  if (typeof fromIndex === 'number') props.reorderStatusUpdateScreens(fromIndex, index);
                  props.setStatusUpdateDragIndex(null);
                  props.setStatusUpdateDragOverIndex(null);
                }}
                onDragEnd={() => {
                  props.setStatusUpdateDragIndex(null);
                  props.setStatusUpdateDragOverIndex(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: isDragOver ? '#fff5f5' : '#fafafa',
                  border: isDragOver ? '1.5px solid #c00' : '1px solid #eee',
                  borderRadius: 12,
                  padding: '10px 12px',
                  opacity: isDragging ? 0.45 : 1,
                  cursor: props.statusUpdateScreensLoading || props.statusUpdateScreensSaving ? 'default' : 'grab',
                  boxShadow: isDragOver ? '0 2px 10px rgba(204,0,0,0.12)' : 'none',
                  transition: 'background 0.15s, border-color 0.15s, opacity 0.15s',
                }}
              >
                <span
                  title="Ziehen zum Sortieren"
                  style={{ color: '#bbb', fontSize: 15, letterSpacing: -1, userSelect: 'none', lineHeight: 1 }}
                  aria-hidden
                >
                  ⋮⋮
                </span>
                <span style={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: screen.enabled ? '#c00' : '#ccc',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 13,
                }}>
                  {index + 1}
                </span>
                <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={screen.enabled}
                    disabled={
                      props.statusUpdateScreensLoading ||
                      props.statusUpdateScreensSaving ||
                      (screen.enabled && props.statusUpdateScreens.filter((s) => s.enabled).length <= 1)
                    }
                    onChange={() => props.toggleStatusUpdateScreen(index)}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, accentColor: '#c00' }}
                  />
                  <span style={{ fontWeight: 700, color: screen.enabled ? '#222' : '#999' }}>{screen.label}</span>
                </label>
              </div>
            );
          })}
        </div>
        {props.statusUpdateScreensError && (
          <div style={{ color: '#c00', fontSize: 13, fontWeight: 600 }}>
            Speichern fehlgeschlagen: {props.statusUpdateScreensError}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#888' }}>
          Per Drag & Drop sortieren — die PWA übernimmt die Reihenfolge bei der nächsten Statusabfrage.
        </div>
      </SettingsCard>

      <div className="portal-section-label">Stammdaten</div>
      <div className="portal-grid">
        <SettingsCard icon={faRadio} title="Funkkanäle" description="Vorgaben für die Kanalwahl in der App.">
          <AddRow
            value={props.newFunkkanal}
            onChange={props.setNewFunkkanal}
            onAdd={props.saveStandardFunkkanaele}
            placeholder="Neuer Funkkanal"
          />
          <ChipList
            items={props.radioChannelsWithIds.map((channel, i) => ({
              key: channel.id || i,
              label: channel.name || '',
              onRemove: () => props.deleteRadioChannel(channel.id!, channel.name || ''),
            }))}
          />
        </SettingsCard>

        <SettingsCard icon={faTags} title="Aufgaben" description="Standard-Tasks beim Truppstart.">
          <AddRow
            value={props.newTask}
            onChange={props.setNewTask}
            onAdd={props.saveStandardTasks}
            placeholder="Neue Aufgabe"
          />
          <ChipList
            items={props.tasksWithIds.map((task, i) => ({
              key: task.id || i,
              label: task.name,
              onRemove: () => props.deleteTask(task.id, task.name),
            }))}
          />
        </SettingsCard>

        <SettingsCard icon={faComment} title="Feedbacks" description="Auswahl beim Status-Feedback.">
          <AddRow
            value={props.newFeedback}
            onChange={props.setNewFeedback}
            onAdd={props.saveStandardFeedbacks}
            placeholder="Neues Feedback"
          />
          <ChipList
            items={props.feedbacksWithIds.map((fb, i) => ({
              key: fb.id || i,
              label: fb.text,
              onRemove: () => props.deleteFeedback(fb.id, fb.text),
            }))}
          />
        </SettingsCard>

        <SettingsCard icon={faMapMarkerAlt} title="Standorte" description="Vorgeschlagene Einsatzorte.">
          <AddRow
            value={props.newStandort}
            onChange={props.setNewStandort}
            onAdd={props.saveStandardStandorte}
            placeholder="Neuer Standort"
          />
          <ChipList
            items={props.locationsWithIds.map((location, i) => ({
              key: location.id || i,
              label: location.name || '',
              onRemove: () => props.deleteLocation(location.id!, location.name || ''),
            }))}
          />
        </SettingsCard>
      </div>

      <div className="portal-section-label">Einsatztypen</div>
      <SettingsCard
        icon={faFire}
        title="Verfügbare Einsatztypen"
        description="Welche Einsatztypen für diese Organisation freigeschaltet sind."
      >
        {props.loadingOperationTypes ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>Einsatztypen werden geladen…</div>
        ) : props.errorOperationTypes ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#c91f0c' }}>Fehler: {props.errorOperationTypes}</div>
        ) : props.allOperationTypes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>Keine Einsatztypen verfügbar</div>
        ) : (
          <div style={{
            maxHeight: 320,
            overflowY: 'auto',
            borderRadius: 12,
            border: '1px solid #eee',
            background: '#fafafa',
          }}>
            {props.allOperationTypes.map((opType, i) => (
              <div
                key={opType.id || i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderBottom: i < props.allOperationTypes.length - 1 ? '1px solid #eee' : 'none',
                  background: opType.assigned ? 'rgba(46, 125, 50, 0.06)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: opType.assigned ? '#2e7d32' : '#ccc',
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{opType.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (opType.assigned) props.removeOperationTypeFromOrg(opType.id);
                    else props.addOperationTypeToOrg(opType.id);
                  }}
                  disabled={props.loadingOperationTypes}
                  style={{
                    background: opType.assigned ? '#fff' : '#2e7d32',
                    color: opType.assigned ? '#c00' : '#fff',
                    border: opType.assigned ? '1.5px solid #c00' : 'none',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  {opType.assigned ? 'Entfernen' : 'Aktivieren'}
                </button>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
};

export default SettingsPanel;
